import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import firebase from 'firebase/compat/app';
import 'firebase/compat/auth';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';

@Component({
    selector: 'edit-profile',
    templateUrl: './edit-profile.component.html',
    styleUrls: ['./edit-profile.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class EditProfileComponent implements OnInit
{
    profileForm: FormGroup;
    passwordForm: FormGroup;
    user: User;
    activeTab: 'profile' | 'password' = 'profile';
    saving: boolean = false;
    hideCurrentPassword: boolean = true;
    hideNewPassword: boolean = true;
    hideConfirmPassword: boolean = true;
    message: { type: 'success' | 'error'; text: string } = null;
    private _initialProfileValue: { firstName: string; lastName: string; phoneNumber: string } = null;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: { user: User },
        private _dialogRef: MatDialogRef<EditProfileComponent>,
        private _formBuilder: FormBuilder,
        private _userService: UserService,
        private _angularFirestore: AngularFirestore,
        private _angularFireAuth: AngularFireAuth,
        private _angularFireDatabase: AngularFireDatabase
    )
    {
    }

    ngOnInit(): void
    {
        this.user = this.data?.user || JSON.parse(sessionStorage.getItem('user'));

        this.profileForm = this._formBuilder.group({
            email: [{ value: this.user?.email || '', disabled: true }],
            firstName: [this.user?.firstName || '', Validators.required],
            lastName: [this.user?.lastName || '', Validators.required],
            phoneNumber: [this.formatPhoneForDisplay(this.user?.phoneNumber), this.phoneNumberValidator]
        });

        this.passwordForm = this._formBuilder.group({
            currentPassword: ['', Validators.required],
            newPassword: ['', Validators.minLength(6)],
            confirmNewPassword: ['']
        }, {
            validators: this.passwordFieldsValid
        });

        this.setInitialProfileValue();

        if (this.user?.id) {
            this._angularFirestore.firestore.collection('users').doc(this.user.id).get().then(snapshot => {
                if (!snapshot.exists) {
                    return;
                }

                const firestoreUser = snapshot.data() as User;
                this.user = {
                    ...this.user,
                    ...firestoreUser
                };

                this.profileForm.patchValue({
                    email: this.user.email || '',
                    firstName: this.user.firstName || '',
                    lastName: this.user.lastName || '',
                    phoneNumber: this.formatPhoneForDisplay(this.user.phoneNumber)
                });
                this.setInitialProfileValue();
            });
        }
    }

    get canSaveProfile(): boolean
    {
        return !this.saving && this.profileForm.valid && this.hasProfileChanges();
    }

    get canSavePassword(): boolean
    {
        return !this.saving && this.passwordForm.valid;
    }

    setActiveTab(tab: 'profile' | 'password'): void
    {
        this.activeTab = tab;
        this.message = null;
    }

    async saveProfile(): Promise<void>
    {
        if (!this.canSaveProfile) {
            this.profileForm.markAllAsTouched();
            return;
        }

        this.saving = true;
        this.message = null;

        try {
            const authUser = await this._angularFireAuth.currentUser;

            if (!authUser || !authUser.email || !this.user?.id) {
                throw new Error('Unable to find the signed in user.');
            }

            const rawValue = this.profileForm.getRawValue();

            const updatedUser: User = {
                ...this.user,
                firstName: rawValue.firstName,
                lastName: rawValue.lastName,
                phoneNumber: this.toStoredPhoneNumber(rawValue.phoneNumber)
            };

            const profileUpdate = {
                firstName: updatedUser.firstName,
                lastName: updatedUser.lastName,
                phoneNumber: updatedUser.phoneNumber
            };

            const userListUpdate = {
                firstName: updatedUser.firstName,
                lastName: updatedUser.lastName,
                phoneNumber: updatedUser.phoneNumber
            };

            await this._angularFirestore.firestore.collection('users').doc(this.user.id).update(profileUpdate);
            await this._angularFireDatabase.object(`userList/${this.user.id}`).update(userListUpdate).catch(() => null);
            await authUser.updateProfile({
                displayName: `${updatedUser.firstName} ${updatedUser.lastName}`.trim()
            });

            this._userService.user = updatedUser;
            sessionStorage.setItem('user', JSON.stringify({
                ...JSON.parse(sessionStorage.getItem('user')),
                ...updatedUser
            }));
            this.user = updatedUser;
            this.setInitialProfileValue();

            this.message = {
                type: 'success',
                text: 'Profile updated successfully.'
            };
        } catch (error) {
            this.message = {
                type: 'error',
                text: this.getErrorMessage(error)
            };
        } finally {
            this.saving = false;
        }
    }

    onPhoneInput(event: Event): void
    {
        const input = event.target as HTMLInputElement;
        const formattedValue = this.formatPhoneForDisplay(input.value);

        this.profileForm.get('phoneNumber').setValue(formattedValue, {
            emitEvent: false
        });
    }

    async savePassword(): Promise<void>
    {
        if (!this.canSavePassword) {
            this.passwordForm.markAllAsTouched();
            return;
        }

        this.saving = true;
        this.message = null;

        try {
            const authUser = await this._angularFireAuth.currentUser;

            if (!authUser || !authUser.email) {
                throw new Error('Unable to find the signed in user.');
            }

            const rawValue = this.passwordForm.getRawValue();
            const credential = firebase.auth.EmailAuthProvider.credential(authUser.email, rawValue.currentPassword);
            await authUser.reauthenticateWithCredential(credential);
            await authUser.updatePassword(rawValue.newPassword);

            this.passwordForm.reset();
            this.message = {
                type: 'success',
                text: 'Password updated successfully.'
            };
        } catch (error) {
            this.message = {
                type: 'error',
                text: this.getErrorMessage(error)
            };
        } finally {
            this.saving = false;
        }
    }

    private passwordFieldsValid(group: FormGroup): { currentPasswordRequired?: boolean; newPasswordRequired?: boolean; passwordMismatch?: boolean } | null
    {
        const currentPassword = group.get('currentPassword')?.value;
        const newPassword = group.get('newPassword')?.value;
        const confirmNewPassword = group.get('confirmNewPassword')?.value;
        const errors: { currentPasswordRequired?: boolean; newPasswordRequired?: boolean; passwordMismatch?: boolean } = {};

        if (!currentPassword) {
            errors.currentPasswordRequired = true;
        }

        if (!newPassword) {
            errors.newPasswordRequired = true;
        }

        if (newPassword !== confirmNewPassword) {
            errors.passwordMismatch = true;
        }

        return Object.keys(errors).length ? errors : null;
    }

    private setInitialProfileValue(): void
    {
        const rawValue = this.profileForm.getRawValue();

        this._initialProfileValue = {
            firstName: this.normalizeValue(rawValue.firstName),
            lastName: this.normalizeValue(rawValue.lastName),
            phoneNumber: this.getPhoneDigits(rawValue.phoneNumber)
        };
    }

    private hasProfileChanges(): boolean
    {
        if (!this._initialProfileValue) {
            return false;
        }

        const rawValue = this.profileForm.getRawValue();

        return this.normalizeValue(rawValue.firstName) !== this._initialProfileValue.firstName
            || this.normalizeValue(rawValue.lastName) !== this._initialProfileValue.lastName
            || this.getPhoneDigits(rawValue.phoneNumber) !== this._initialProfileValue.phoneNumber;
    }

    private normalizeValue(value): string
    {
        return (value ?? '').toString().trim();
    }

    private phoneNumberValidator(control: AbstractControl): ValidationErrors | null
    {
        const digits = (control.value ?? '').toString().replace(/\D/g, '');

        if (!digits) {
            return null;
        }

        return digits.length === 10 ? null : { phoneNumber: true };
    }

    private getPhoneDigits(value): string
    {
        const digits = (value ?? '').toString().replace(/\D/g, '');

        if (digits.length === 11 && digits.startsWith('1')) {
            return digits.slice(1);
        }

        return digits.slice(0, 10);
    }

    private formatPhoneForDisplay(value): string
    {
        const digits = this.getPhoneDigits(value);

        if (digits.length <= 3) {
            return digits;
        }

        if (digits.length <= 6) {
            return `${digits.slice(0, 3)}-${digits.slice(3)}`;
        }

        return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
    }

    private toStoredPhoneNumber(value): string | null
    {
        const digits = this.getPhoneDigits(value);

        return digits ? `+1${digits}` : null;
    }

    private getErrorMessage(error): string
    {
        switch (error?.code) {
            case 'auth/wrong-password':
            case 'auth/invalid-credential':
                return 'Current password is incorrect.';
            case 'auth/weak-password':
                return 'New password should be at least 6 characters.';
            case 'auth/requires-recent-login':
                return 'Please sign out, sign back in, and try again.';
            default:
                return error?.message || 'Unable to update profile. Please try again.';
        }
    }
}
